import FilterCountPill from "@/components/molecules/FiltersActive/FilterCountPill";
import FilterPill from "@/components/molecules/FiltersActive/FilterPill";
import ToggleAllButton from "@/components/molecules/FiltersActive/ToggleAllButton";
import {
  RawFilterItem,
  buildRawFilterItems,
} from "@/components/molecules/FiltersActive/filtersData";
import {
  clearSbcParams,
  sbcFiltersSelector,
} from "@/redux/sbcs/sbcFiltersSlice";
import { AppDispatch } from "@/redux/store";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

const FiltersActive: React.FC<{ renderHorizontally?: boolean }> = ({
  renderHorizontally,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const [showAll, setShowAll] = useState(false);
  const filterState = useSelector(sbcFiltersSelector);

  const rawItems = useMemo<RawFilterItem[]>(
    () => buildRawFilterItems(filterState, dispatch),
    [filterState, dispatch],
  );

  const filterItems = useMemo(() => {
    return rawItems
      .filter((i) => i.condition)
      .map((i) => {
        let icon = i.icon;

        if (i.imageSrc) {
          icon = <img src={i.imageSrc} alt={i.alt} className="h-7 w-7" />;
        } else if (typeof i.icon === "function") {
          icon = i.icon();
        }

        return {
          condition: true,
          alt: i.alt,
          icon,
          onClick: i.onClick,
        };
      });
  }, [rawItems]);

  const total = filterItems.length;

  const shouldShowCountPill = !showAll && total > 3;

  const hasOtherFilters = filterItems.length > 1;

  const showClearAllTop =
    !showAll &&
    filterItems.length <= 3 &&
    (filterState.playersFrom !== "market" || hasOtherFilters);

  const showToggleAll = total > 3;
  const noFiltersApplied = () => {
    const defaultFilter =
      filterItems.length === 1 && filterState.playersFrom === "market";

    if (defaultFilter) {
      return (
        <div className="flex items-center text-sm text-dark-disabled">
          No filters applied
        </div>
      );
    }
    return null;
  };

  const topRow = shouldShowCountPill
    ? filterItems.slice(0, 2)
    : filterItems.slice(0, 3);

  const rest = filterItems.slice(topRow.length);

  return renderHorizontally ? (
    <div className="flex flex-row py-3 px-4 gap-2 overflow-x-auto">
      {filterItems.map((item, index) => (
        <FilterPill key={index + "horizontal"} item={item} />
      ))}
    </div>
  ) : (
    <div className="py-4">
      <div className="flex flex-wrap items-center justify-start gap-1.5 px-4">
        <div className="w-full flex flex-row justify-between">
          <div className="flex flex-row gap-1.5 items-center">
            {topRow.map((item, index) => (
              <FilterPill key={index + "top"} item={item} />
            ))}
            {shouldShowCountPill && <FilterCountPill count={rest.length} />}
          </div>
          <div className="flex justify-center items-center">
            {noFiltersApplied()}
          </div>
          {showClearAllTop && (
            <button
              className="rounded-full h-9 px-2 bg-[#49535F] hover:bg-[#5F6C7C] text-dark-secondary hover:text-dark-primary flex items-center justify-center text-nowrap"
              onClick={() => {
                dispatch(clearSbcParams());
              }}
            >
              Clear all
            </button>
          )}

          {showToggleAll && (
            <ToggleAllButton
              showAll={showAll}
              onToggle={() => setShowAll((prev) => !prev)}
            />
          )}
        </div>
      </div>

      {showAll && rest.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5 px-4 pt-2">
          {rest.map((item, index) => (
            <FilterPill key={index + "rest"} item={item} />
          ))}
          {(filterState.playersFrom !== "market" || hasOtherFilters) && (
            <button
              className="rounded-full h-9 px-2 bg-[#49535F] hover:bg-[#5F6C7C] text-dark-secondary hover:text-dark-primary flex items-center justify-center ml-auto"
              onClick={() => {
                dispatch(clearSbcParams());
                setShowAll(false);
              }}
            >
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default FiltersActive;
